import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import {
  buildChangelogPreviewShellHtml,
  buildChangelogBody,
  type ChangelogPreviewData,
} from "./changelog-preview-html-builder";

interface ChangelogPreviewIframeProps {
  previewData: ChangelogPreviewData;
  className?: string;
}

export function ChangelogPreviewIframe({
  previewData,
  className = "",
}: ChangelogPreviewIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const bodyHtml = buildChangelogBody(previewData);
  const debouncedHtml = useDebounce(bodyHtml, 100);

  const shellHtml = useMemo(() => buildChangelogPreviewShellHtml(), []);

  useEffect(() => {
    if (!isLoaded) return;

    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc?.body) return;

    doc.body.innerHTML = debouncedHtml;
  }, [debouncedHtml, isLoaded]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className={`relative h-full w-full ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/50">
          <div className="flex flex-col items-center gap-2">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-muted-foreground text-xs">
              미리보기 로딩 중...
            </span>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        srcDoc={shellHtml}
        title="체인지로그 미리보기"
        sandbox="allow-same-origin"
        onLoad={handleLoad}
        className="h-full w-full border-0"
      />
    </div>
  );
}
